import React from "react";
import {
  Stack,
  Typography,
  Divider,
  Avatar,
  Box,
  Tooltip,
} from "@mui/material";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import LoginIcon from "@mui/icons-material/Login";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SecurityIcon from "@mui/icons-material/Security";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

const getHealthBgColor = (color) => {
  const map = {
    success: "#4caf50",
    error: "#f44336",
    warning: "#ff9800",
    default: "#9e9e9e",
  };
  return map[color] || map.default;
};

const IdentitySection = ({
  authProvider,
  lastVerifiedAt,
  lastLoginAt,
  currentLoginAt,
  healthLabel,
  healthColor,
  typeLabel,
  roleLabel,
  isRoot,
}) => {
  const captionStyle = {
    variant: "caption",
    color: "text.secondary",
    fontWeight: 800,
  };
  const bodyStyle = { variant: "body2", color: "text.secondary" };

  return (
    <Stack spacing={2.5}>
      <Box>
        <Stack direction='row' spacing={1} alignItems='center' mb={0.5}>
          <FingerprintIcon fontSize='small' color='action' />
          <Typography {...captionStyle}>Authentication Fingerprint</Typography>
        </Stack>
        <Stack spacing={1} pl={4}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <SecurityIcon fontSize='small' sx={{ fontSize: 16 }} />
            <Typography {...bodyStyle}>
              Provider: {String(authProvider).toUpperCase()}
            </Typography>
          </Stack>
          <Stack direction='row' alignItems='center' spacing={1}>
            <AccessTimeIcon fontSize='small' sx={{ fontSize: 16 }} />
            <Typography {...bodyStyle}>
              Last verification: {lastVerifiedAt}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Stack direction='row' spacing={1} alignItems='center' mb={0.5}>
          <LoginIcon fontSize='small' color='action' />
          <Typography {...captionStyle}>Login Activity</Typography>
        </Stack>
        <Stack spacing={1} pl={4}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <AccessTimeIcon fontSize='small' sx={{ fontSize: 16 }} />
            <Typography {...bodyStyle}>Last login: {lastLoginAt}</Typography>
          </Stack>
          <Stack direction='row' alignItems='center' spacing={1}>
            <AccessTimeIcon fontSize='small' sx={{ fontSize: 16 }} />
            <Typography {...bodyStyle}>Current: {currentLoginAt}</Typography>
          </Stack>
        </Stack>
      </Box>

      <Stack
        direction='row'
        alignItems='center'
        spacing={2}
        sx={{
          p: 1.5,
          borderRadius: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* 左侧：带呼吸灯的健康状态 */}
        <Stack
          direction='row'
          alignItems='center'
          spacing={1.5}
          sx={{ pr: 2, borderRight: "1px solid", borderColor: "divider" }}
        >
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: getHealthBgColor(healthColor),
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: -4,
                  left: -4,
                  right: -4,
                  bottom: -4,
                  borderRadius: "50%",
                  border: `2px solid ${getHealthBgColor(healthColor)}`,
                  animation: "ripple 1.2s infinite ease-in-out", // 需在 theme 或全局 css 定义 keyframes
                },
              }}
            />
          </Box>
          <Typography variant='body2' fontWeight={800} color='text.primary'>
            Status: {healthLabel}
          </Typography>
        </Stack>

        {/* 右侧：身份与权限 */}
        <Stack direction='row' alignItems='center' spacing={1}>
          {isRoot ? (
            <Tooltip title='Root Administrator'>
              <Avatar sx={{ bgcolor: "warning.main", width: 28, height: 28 }}>
                <AdminPanelSettingsIcon fontSize='small' />
              </Avatar>
            </Tooltip>
          ) : (
            <Tooltip title='Normal User'>
              <Avatar sx={{ bgcolor: "primary.main", width: 28, height: 28 }}>
                <SecurityIcon fontSize='small' />
              </Avatar>
            </Tooltip>
          )}
          <Stack direction='column'>
            <Typography variant='caption' color='text.secondary' lineHeight={1}>
              Current Role
            </Typography>
            <Typography variant='body2' fontWeight={700} lineHeight={1.2}>
              {isRoot ? "Root Administrator" : `${typeLabel} / ${roleLabel}`}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default IdentitySection;
